/* ============================================
   THESIS PAGE
   The Creative Orchestra - GEMINI Framework
   PhD Thesis Colloquium and Defense presentations
   ============================================ */

import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  GraduationCap,
  BookOpen,
  Sparkles,
  Calendar,
  Camera,
  Award,
} from "lucide-react";
import { ScrollReveal } from "@components/common";
import SlideDeckViewer from "@components/common/SlideDeckViewer";
import ThesisBookReader from "@components/common/ThesisBookReader";
import { SITE_INFO } from "@utils/constants";

const COLLOQUIUM_PDF_URL =
  "/assets/thesis_presentation/PhD_Thesis_Colloquium_Presentation.pdf";
const DEFENSE_PDF_URL =
  "/assets/thesis_presentation/PhD_Thesis_Defense_Presentation.pdf";
const DEFENSE_PHOTO_URL = "/assets/images/thesis/phd-defense-day.jpg";
// The thesis is served as pre-rendered page images, never as a PDF.
// Generate with `npm run thesis:pages`.
const THESIS_BOOK_MANIFEST = "/assets/thesis_pages/manifest.json";

/**
 * Thesis Page Component
 * Displays thesis overview and dated colloquium / defense slide decks
 */
const ThesisPage = () => {
  useEffect(() => {
    document.title = `Thesis Presentation | ${SITE_INFO.title}`;
  }, []);

  return (
    <div className="min-h-screen py-20">
      <div className="max-w-5xl mx-auto px-4">
        {/* Hero Header */}
        <ScrollReveal>
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div
              className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-indigo-500/10
              flex items-center justify-center border border-indigo-500/20"
            >
              <GraduationCap className="w-10 h-10 text-indigo-400" />
            </div>

            <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
              PhD Thesis
            </h1>

            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-6">
              The Creative Orchestra — A Designer's Quest to Humanize Artificial
              Intelligence
            </p>

            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full
              bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm"
            >
              <Award className="w-4 h-4" />
              Successfully defended on 12 August 2026
            </div>
          </motion.div>
        </ScrollReveal>

        {/* Thesis Overview */}
        <ScrollReveal>
          <div className="mb-16">
            <motion.div
              className="relative p-8 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/50 to-transparent" />

              <div className="flex items-start gap-4 mb-4">
                <div className="p-2 rounded-lg bg-indigo-500/10">
                  <BookOpen className="w-5 h-5 text-indigo-400" />
                </div>
                <h2 className="text-xl font-semibold text-white pt-1">
                  About the Research
                </h2>
              </div>

              <div className="space-y-4 text-gray-300 leading-relaxed pl-12">
                <p>
                  This doctoral research, conducted at the{" "}
                  <span className="text-white font-medium">
                    Indian Institute of Science (IISc), Bangalore
                  </span>
                  , investigates a fundamental question in design and artificial
                  intelligence:{" "}
                  <em className="text-indigo-300">
                    Can machines become true creative partners rather than
                    passive tools?
                  </em>
                </p>
                <p>
                  Over five years, this work developed the{" "}
                  <span className="text-white font-medium">
                    GEMINI Framework
                  </span>{" "}
                  — an ecosystem of nine interconnected "Creative Machines" that
                  together transform the design process from Computer-Aided
                  Design (CAD) to{" "}
                  <span className="text-white font-medium">
                    Designers with Agentic Computers (DAC)
                  </span>
                  . Each system addresses a distinct phase of the design journey
                  — Generation, Evaluation, Management, and INspiration — while
                  maintaining deep integration within the broader ecosystem.
                </p>
                <p>
                  The research introduces new vocabularies for human-AI
                  collaboration in design, demonstrating that human creativity
                  and machine intelligence are not competing forces but
                  complementary ones. From fine-tuned language models for
                  ideation to eye-tracking-based inspiration capture, from
                  multi-agent orchestration to physics-informed sketch
                  understanding, the GEMINI systems collectively represent a
                  paradigm shift in how designers work with intelligent
                  machines.
                </p>
              </div>
            </motion.div>
          </div>
        </ScrollReveal>

        {/* Thesis Colloquium */}
        <section id="colloquium" className="mb-24">
          <MilestoneHeader date="28 January 2026" title="Thesis Colloquium" />

          <ScrollReveal>
            <motion.div
              className="relative p-8 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="p-2 rounded-lg bg-indigo-500/10">
                  <Sparkles className="w-5 h-5 text-indigo-400" />
                </div>
                <h3 className="text-xl font-semibold text-white pt-1">
                  Thesis Colloquium Presentation
                </h3>
              </div>

              <div className="space-y-4 text-gray-300 leading-relaxed pl-12">
                <p>
                  The presentation below was delivered as part of the PhD thesis
                  colloquium on 28th January 2026, at IISc Bangalore. It walks
                  through the complete research journey — from identifying the
                  fundamental challenges in design-AI interaction, to the
                  development and validation of each GEMINI system, and finally
                  the synthesis of the overarching framework and its
                  implications for the future of human-AI co-creation.
                </p>
                <p className="text-sm text-gray-500">
                  Use the arrow keys or the navigation controls below to browse
                  through the slides. Click the fullscreen button for a better
                  viewing experience.
                </p>
              </div>
            </motion.div>
          </ScrollReveal>

          <ScrollReveal>
            <SlideDeckViewer
              url={COLLOQUIUM_PDF_URL}
              title="PhD Thesis Colloquium Presentation"
            />
          </ScrollReveal>
        </section>

        {/* Thesis Defense */}
        <section id="defense" className="mb-24">
          <MilestoneHeader date="12 August 2026" title="Thesis Defense" />

          <ScrollReveal>
            <motion.div
              className="relative p-8 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent" />

              <div className="flex items-start gap-4 mb-4">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <GraduationCap className="w-5 h-5 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white pt-1">
                  Thesis Defense Presentation
                </h3>
              </div>

              <div className="space-y-4 text-gray-300 leading-relaxed pl-12">
                <p>
                  The PhD thesis was successfully defended on{" "}
                  <span className="text-white font-medium">
                    12th August 2026
                  </span>{" "}
                  at the{" "}
                  <span className="text-white font-medium">
                    Indian Institute of Science (IISc), Bangalore
                  </span>
                  .
                </p>

                <div className="p-5 rounded-xl bg-white/[0.03] border border-white/10">
                  <p className="text-xs uppercase tracking-widest text-emerald-400/80 mb-2">
                    Computer-Aided Design to Designers with Agentic Computers
                  </p>
                  <p className="text-lg text-white font-medium leading-snug mb-3">
                    An Active Ideation Framework using Creative Machines for
                    Synergistic Human-AI Collaboration
                  </p>
                  <p className="text-sm text-indigo-300 italic">
                    Machines that Understand, Support, and Amplify the Creative
                    Act of Design
                  </p>
                </div>

                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-400">
                  <span>
                    Presenter:{" "}
                    <span className="text-gray-200">{SITE_INFO.author}</span>
                  </span>
                  <span>
                    Supervisor:{" "}
                    <span className="text-gray-200">Prof. Dibakar Sen</span>
                  </span>
                </div>

                <p>
                  The presentation below was delivered as the PhD thesis
                  defense. It presents the complete research contribution — the
                  shift from Computer-Aided Design to Designers with Agentic
                  Computers — and the GEMINI framework of creative machines that
                  understand, support, and amplify the creative act of design.
                </p>
                <p className="text-sm text-gray-500">
                  Use the arrow keys or the navigation controls below to browse
                  through the slides. Click the fullscreen button for a better
                  viewing experience.
                </p>
              </div>
            </motion.div>
          </ScrollReveal>

          {/* Defense day photograph */}
          <ScrollReveal>
            <motion.figure
              className="mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative rounded-2xl overflow-hidden bg-black">
                <img
                  src={DEFENSE_PHOTO_URL}
                  alt="Sankar Balasubramanian presenting his PhD thesis defense at IISc Bangalore on 12 August 2026"
                  className="w-full h-auto select-none pointer-events-none"
                  draggable="false"
                  loading="lazy"
                  onContextMenu={(e) => e.preventDefault()}
                />

                {/* Vignette: darkens evenly inward from every edge so the
                    photograph settles into the dark page instead of
                    ending on a hard rectangle. */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse 78% 78% at 50% 48%, transparent 40%, rgba(0,0,0,0.28) 68%, rgba(0,0,0,0.72) 88%, rgba(0,0,0,0.95) 100%)",
                  }}
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    boxShadow:
                      "inset 0 0 70px 24px rgba(0,0,0,0.75), inset 0 0 160px 60px rgba(0,0,0,0.45)",
                  }}
                />
              </div>

              <figcaption className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                <Camera className="w-4 h-4 text-emerald-400/70" />
                Moments before the defense — IISc Bangalore, 12 August 2026
              </figcaption>
            </motion.figure>
          </ScrollReveal>

          <ScrollReveal>
            <SlideDeckViewer
              url={DEFENSE_PDF_URL}
              title="PhD Thesis Defense Presentation"
            />
          </ScrollReveal>
        </section>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        {/* Thesis Book */}
        <section id="thesis-book">
          <ScrollReveal>
            <div className="text-center mb-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent to-white/20" />
                <div className="flex items-center gap-2 text-indigo-400">
                  <BookOpen className="w-4 h-4" />
                  <span className="text-sm font-medium tracking-[0.2em] uppercase">
                    The Dissertation
                  </span>
                </div>
                <div className="flex-1 h-px bg-gradient-to-l from-transparent to-white/20" />
              </div>
              <h2 className="text-2xl md:text-3xl font-display font-bold text-white">
                Thesis Book
              </h2>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <motion.div
              className="relative p-8 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm mb-10"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="p-2 rounded-lg bg-indigo-500/10">
                  <BookOpen className="w-5 h-5 text-indigo-400" />
                </div>
                <h3 className="text-xl font-semibold text-white pt-1">
                  Read the complete thesis
                </h3>
              </div>

              <div className="space-y-4 text-gray-300 leading-relaxed pl-12">
                <p>
                  The full doctoral dissertation is bound below as an
                  interactive book. Hover a page edge and turn the leaf. The
                  book opens as a two-page spread.
                </p>
                <p className="text-sm text-gray-500">
                  Use the arrows, the page slider, or fullscreen for a larger
                  reading desk. Switch to Pages for one large page at a time,
                  and zoom in for finer type. The dissertation is available for
                  reading here only.
                </p>
              </div>
            </motion.div>
          </ScrollReveal>

          <ThesisBookReader
            manifestUrl={THESIS_BOOK_MANIFEST}
            title="PhD Thesis Book"
          />
        </section>
      </div>
    </div>
  );
};

/**
 * Date-led header that separates colloquium and defense milestones
 */
const MilestoneHeader = ({ date, title }) => {
  return (
    <ScrollReveal>
      <div className="text-center mb-10">
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-white/20" />
          <div className="flex items-center gap-2 text-indigo-400">
            <Calendar className="w-4 h-4" />
            <time className="text-sm font-medium tracking-[0.2em] uppercase">
              {date}
            </time>
          </div>
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-white/20" />
        </div>
        <h2 className="text-2xl md:text-3xl font-display font-bold text-white">
          {title}
        </h2>
      </div>
    </ScrollReveal>
  );
};

export default ThesisPage;
